import React, { useRef, useEffect, useState } from 'react';
import { useStateValue } from './StateProvider';
import ReactToPrint from 'react-to-print';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DatePicker from "react-datepicker";
import Send from './Components/send.js'
import Select from './Components/select.js';

import {
    getClientAccountSOA, getSupplierAccountSOA, getIncomeAccountSOA, getExpenseAccountSOA, getCashAccountSOA, getReceiptsSOA, getItemsSOA,
    getLiabilityAccountSOA, getStaffSOA, getEquityAccountSOA, getAssetAccountSOA
} from './Utils/statementaccount.js';
import { registerArabicFont } from "./Utils/registerarabicfont.js";

import './StatementAccount.css';

function StatementAccount({ state, setState, type, account }) {
    const [{ project }, dispatch] = useStateValue();

    const [from, setFrom] = useState(account.from ? new Date(account.from * 1000) : new Date());
    const [fromts, setFromTs] = useState(account.from);
    const [to, setTo] = useState(account.to ? new Date(account.to * 1000) : new Date());
    const [tots, setToTs] = useState(account.to);

    const printRef = useRef();

    const handleCloseModal = () => {
        setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
    }

    const sendButton = ({ templates, contact, context }) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <Send templates={templates} contact={contact} context={context} state={state} setState={setState} />, modaltype: 'small', modaltitle: 'Send' }));
    }

    const handleFromChange = (date) => {
        setFrom(date);
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);
        setFromTs(Math.floor(startOfDay.getTime() / 1000));
    };

    const handleToChange = (date) => {
        setTo(date);
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);
        setToTs(Math.floor(endOfDay.getTime() / 1000));
    };

    function ClientAccountSOA() {
        const [accountid, setAccountid] = useState(account.id);
        const [accountname, setAccountname] = useState(account.name);
        const [selectedsubaccountid, setSelectedsubaccountid] = useState(account.subaccountid);
        const [selectedcurrencyid, setSelectedcurrencyid] = useState(project.basecurrency);
        const [statementdata, setStatementdata] = useState([]);
        const [statementsummary, setStatementsummary] = useState({ currency: '', totaldebit: '', totalcredit: '', total: '' });

        const [loading, setLoading] = useState(false);

        useEffect(() => {
            let context = {
                accountid: accountid,
                ...project,
                fromts: fromts,
                tots: tots,
                selectedsubaccountid: selectedsubaccountid,
                selectedcurrencyid: selectedcurrencyid
            }
            const clientaccountsoa = getClientAccountSOA(context);

            setStatementdata(clientaccountsoa.data);
            setStatementsummary(clientaccountsoa.summary);
        }, [accountid, project.transactions, project.journalentries, project.exchangerates, project.currencies, from, to, selectedsubaccountid, selectedcurrencyid]);

        const handleSend = () => {
            let client = project.clients.find(client => client.id == accountid);

            sendButton({
                templates: project.templates?.filter(template => template.type == 'statementofaccountclient'),
                contact: client,
                context: {
                    projectname: project.name,
                    currencysymbol: statementsummary.currency,
                    balance: statementsummary.closingbalance.toLocaleString('en-US'),
                    date: moment().format('D/MMM/YYYY'),
                }
            });
        };

        const saveAsPDF = () => {
            setLoading(true);
            try {
                const accountName = accountname.replace(/\s+/g, '_').toLowerCase();
                const currentDate = moment().format('DD_MM_YYYY');
                const currencyName = project.currencies.find(cur => cur.id == selectedcurrencyid)?.name;
                const subaccountName = (project.subaccounts.find(sub => sub.id == selectedsubaccountid)?.name || 'All');

                const doc = new jsPDF();
                registerArabicFont(doc);

                let currentY = 10;

                currentY += 10;
                doc.setFontSize(24);
                const pageWidth = doc.internal.pageSize.getWidth();
                const title = 'Statement of Account';
                const titleWidth = doc.getTextWidth(title);
                doc.text(title, (pageWidth - titleWidth) / 2, currentY);

                currentY += 10;
                doc.setFontSize(10);
                doc.text('Project Name: ' + project.name, 10, currentY);

                currentY += 5;
                doc.text('Account Name: ' + accountname, 10, currentY);

                currentY += 5;
                const formattedFrom = moment.unix(fromts).format('D/MMM/YYYY');
                const formattedTo = moment.unix(tots).format('D/MMM/YYYY');
                doc.text('From: ' + formattedFrom, 10, currentY);

                currentY += 5;
                doc.text('To: ' + formattedTo, 10, currentY);

                currentY += 5;
                doc.text('Subaccount: ' + subaccountName, 10, currentY);

                currentY += 5;
                doc.text('Currency: ' + currencyName, 10, currentY);

                currentY += 5;
                const body = statementdata.map(entry => [
                    entry.ts,
                    entry.type,
                    entry.id,
                    entry.name,
                    entry.debitcurrency + ' ' + entry.debit.toLocaleString('en-US'),
                    entry.creditcurrency + ' ' + entry.credit.toLocaleString('en-US'),
                    entry.balancecurrency + ' ' + entry.balance.toLocaleString('en-US'),
                ]);

                body.push(
                    [
                        { content: 'Total Debit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Total Credit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Closing Balance', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.balance.toLocaleString('en-US'),
                    ]
                );

                autoTable(doc, {
                    head: [['Date', 'Type', 'ID', 'Name', 'Debit', 'Credit', 'Balance']],
                    body,
                    startY: currentY,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [200, 200, 200],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        halign: 'center',
                    },
                    bodyStyles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        font: 'notoarabic',
                    },
                    margin: { top: 10, left: 10, right: 10 },
                });

                doc.save('soa_' + accountName + '_' + currentDate + '.pdf');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>Statement of Account</div>
                        </div>
                        <div className='modal_rowsection'>{project.projectdetails}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Account Name</div>
                        </div>
                        <div className='modal_rowsection'>{accountname}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={from}
                                onChange={handleFromChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={to}
                                onChange={handleToChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Subaccount
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={[{ id: '', name: 'All' }, ...project.subaccounts]} itemid={selectedsubaccountid} setItemid={setSelectedsubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Currency
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={selectedcurrencyid} setItemid={setSelectedcurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Debit</th>
                                        <th>Credit</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        statementdata.map((entry, index) => {
                                            return (
                                                <tr key={'entry' + index} style={{ pageBreakInside: 'avoid' }}>
                                                    <td><div>{entry.date}</div></td>
                                                    <td><div>{entry.type}</div></td>
                                                    <td><div>{entry.id}</div></td>
                                                    <td><div>{entry.name}</div></td>
                                                    <td><div>{entry.debitcurrency + ' ' + entry.debit.toLocaleString('en-US')}</div></td>
                                                    <td><div>{entry.creditcurrency + ' ' + entry.credit.toLocaleString('en-US')}</div></td>
                                                    <td><div className='statementaccount_balance'>{entry.balancecurrency + ' ' + entry.balance.toLocaleString('en-US')}</div></td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                                <tfoot className='soa_tablefooter'>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Debit</td><td>{statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Credit</td><td>{statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Closing Balance</td><td>{statementsummary.currency + ' ' + statementsummary.total.toLocaleString('en-US')}</td></tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_buttons'>
                    <div className='modal_buttoncontainer'>
                        <ReactToPrint
                            trigger={() => (<button className="modal_button">Print</button>)}
                            content={() => printRef.current}
                        />
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={handleSend} disabled={loading}>Send</button>
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={() => saveAsPDF()} disabled={loading}>{loading ? "Saving..." : "Save as PDF"}</button>
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={handleCloseModal}>Cancel</button>
                    </div>
                </div>
            </div>
        )
    }

    function SupplierAccountSOA() {
        const [accountid, setAccountid] = useState(account.id);
        const [accountname, setAccountname] = useState(account.name);
        const [selectedsubaccountid, setSelectedsubaccountid] = useState(account.subaccountid);
        const [selectedcurrencyid, setSelectedcurrencyid] = useState(account.currencyid);
        const [statementdata, setStatementdata] = useState([]);
        const [statementsummary, setStatementsummary] = useState({ currency: '', totaldebit: '', totalcredit: '', total: '' });
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            let context = {
                accountid: accountid,
                ...project,
                fromts: fromts,
                tots: tots,
                selectedsubaccountid: selectedsubaccountid,
                selectedcurrencyid: selectedcurrencyid
            }
            const supplieraccountsoa = getSupplierAccountSOA(context);
            setStatementdata(supplieraccountsoa.data);
            setStatementsummary(supplieraccountsoa.summary)
        }, [accountid, project.transactions, project.journalentries, project.purchaseinvoices, project.exchangerates, project.currencies, from, to, selectedsubaccountid, selectedcurrencyid]);

        const saveAsPDF = async () => {
            setLoading(true);
            try {
                const accountName = accountname.replace(/\s+/g, '_').toLowerCase();
                const currentDate = moment().format('DD_MM_YYYY');
                const currencyName = project.currencies.find(cur => cur.id == selectedcurrencyid)?.name;
                const subaccountName = (project.subaccounts.find(sub => sub.id == selectedsubaccountid)?.name || 'All');

                const doc = new jsPDF();
                registerArabicFont(doc);

                let currentY = 10;

                currentY += 10;
                doc.setFontSize(24);
                const pageWidth = doc.internal.pageSize.getWidth();
                const title = 'Statement of Account';
                const titleWidth = doc.getTextWidth(title);
                doc.text(title, (pageWidth - titleWidth) / 2, currentY);

                currentY += 10;
                doc.setFontSize(10);
                doc.text('Project Name: ' + project.name, 10, currentY);

                currentY += 5;
                doc.text('Account Name: ' + accountname, 10, currentY);

                currentY += 5;
                const formattedFrom = moment.unix(fromts).format('D/MMM/YYYY');
                const formattedTo = moment.unix(tots).format('D/MMM/YYYY');
                doc.text('From: ' + formattedFrom, 10, currentY);

                currentY += 5;
                doc.text('To: ' + formattedTo, 10, currentY);

                currentY += 5;
                doc.text('Subaccount: ' + subaccountName, 10, currentY);

                currentY += 5;
                doc.text('Currency: ' + currencyName, 10, currentY);

                currentY += 5;
                const body = statementdata.map(entry => [
                    entry.ts,
                    entry.type,
                    entry.id,
                    entry.name,
                    entry.debitcurrency + ' ' + entry.debitamount.toLocaleString('en-US'),
                    entry.creditcurrency + ' ' + entry.creditamount.toLocaleString('en-US'),
                    entry.balancecurrency + ' ' + entry.balanceamount.toLocaleString('en-US'),
                ]);

                body.push(
                    [
                        { content: 'Total Debit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Total Credit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Closing Balance', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.closingbalance.toLocaleString('en-US'),
                    ]
                );

                autoTable(doc, {
                    head: [['Date', 'Type', 'ID', 'Name', 'Debit', 'Credit', 'Balance']],
                    body,
                    startY: currentY,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [200, 200, 200],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        halign: 'center',
                    },
                    bodyStyles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        font: 'notoarabic',
                    },
                    margin: { top: 10, left: 10, right: 10 },
                });

                doc.save('soa_' + accountName + '_' + currentDate + '.pdf');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>Statement of Account</div>
                        </div>
                        <div className='modal_rowsection'>{project.projectdetails}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Account Name</div>
                        </div>
                        <div className='modal_rowsection'>{accountname}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={from}
                                onChange={handleFromChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={to}
                                onChange={handleToChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Subaccount
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={[{ id: '', name: 'All' }, ...project.subaccounts]} itemid={selectedsubaccountid} setItemid={setSelectedsubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Currency
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={selectedcurrencyid} setItemid={setSelectedcurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Debit</th>
                                        <th>Credit</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        statementdata.map((entry, index) => {
                                            return (
                                                <tr key={'entry' + index}>
                                                    <td><div>{entry.date}</div></td>
                                                    <td><div>{entry.type}</div></td>
                                                    <td><div>{entry.id}</div></td>
                                                    <td><div>{entry.name}</div></td>
                                                    <td><div>{entry.debitcurrency + ' ' + entry.debit.toLocaleString('en-US')}</div></td>
                                                    <td><div>{entry.creditcurrency + ' ' + entry.credit.toLocaleString('en-US')}</div></td>
                                                    <td><div className='statementaccount_balance'>{entry.balancecurrency + ' ' + entry.balance.toLocaleString('en-US')}</div></td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                                <tfoot className='soa_tablefooter'>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Debit</td><td>{statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Credit</td><td>{statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Closing Balance</td><td>{statementsummary.currency + ' ' + statementsummary.total.toLocaleString('en-US')}</td></tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_buttons'>
                    <div className='modal_buttoncontainer'>
                        <ReactToPrint
                            trigger={() => (<button className="modal_button">Print</button>)}
                            content={() => printRef.current}
                        />
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={() => saveAsPDF()} disabled={loading}>{loading ? "Saving..." : "Save as PDF"}</button>
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={handleCloseModal}>Cancel</button>
                    </div>
                </div>
            </div >
        )
    }

    function IncomeAccountSOA() {
        const [accountid, setAccountid] = useState(account.id);
        const [accountname, setAccountname] = useState(account.name);
        const [selectedsubaccountid, setSelectedsubaccountid] = useState(account.subaccountid);
        const [selectedcurrencyid, setSelectedcurrencyid] = useState(account.currencyid);
        const [statementdata, setStatementdata] = useState([]);
        const [statementsummary, setStatementsummary] = useState({ currency: '', totaldebit: '', totalcredit: '', total: '' });
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            let context = {
                accountid: accountid,
                ...project,
                fromts: fromts,
                tots: tots,
                selectedsubaccountid: selectedsubaccountid,
                selectedcurrencyid: selectedcurrencyid,
            };

            const incomeaccountsoa = getIncomeAccountSOA(context);
            setStatementdata(incomeaccountsoa.data);
            setStatementsummary(incomeaccountsoa.summary);
        }, [accountid, project.salesinvoices, project.journalentries, project.exchangerates, project.currencies, fromts, tots, selectedsubaccountid, selectedcurrencyid]);

        const saveAsPDF = async () => {
            setLoading(true);
            try {
                const accountName = accountname.replace(/\s+/g, '_').toLowerCase();
                const currentDate = moment().format('DD_MM_YYYY');
                const currencyName = project.currencies.find(cur => cur.id == selectedsubaccountid)?.name;

                const doc = new jsPDF();
                registerArabicFont(doc);

                let currentY = 10;

                currentY += 10;
                doc.setFontSize(24);
                const pageWidth = doc.internal.pageSize.getWidth();
                const title = 'Statement of Account';
                const titleWidth = doc.getTextWidth(title);
                doc.text(title, (pageWidth - titleWidth) / 2, currentY);

                currentY += 10;
                doc.setFontSize(10);
                doc.text('Project Name: ' + project.name, 10, currentY);

                currentY += 5;
                doc.text('Account Name: ' + accountName, 10, currentY);

                currentY += 5;
                const formattedFrom = moment.unix(fromts).format('D/MMM/YYYY');
                const formattedTo = moment.unix(tots).format('D/MMM/YYYY');
                doc.text('From: ' + formattedFrom, 10, currentY);

                currentY += 5;
                doc.text('To: ' + formattedTo, 10, currentY);

                currentY += 5;
                doc.text('Currency: ' + currencyName, 10, currentY);

                currentY += 5;
                const body = statementdata.map(entry => [
                    entry.ts,
                    entry.type,
                    entry.id,
                    entry.name,
                    entry.debitcurrency + ' ' + entry.debitamount.toLocaleString('en-US'),
                    entry.creditcurrency + ' ' + entry.creditamount.toLocaleString('en-US'),
                    entry.balancecurrency + ' ' + entry.balanceamount.toLocaleString('en-US'),
                ]);

                body.push(
                    [
                        { content: 'Total Debit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Total Credit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Closing Balance', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.closingbalance.toLocaleString('en-US'),
                    ]
                );

                autoTable(doc, {
                    head: [['Date', 'Type', 'ID', 'Name', 'Debit', 'Credit', 'Balance']],
                    body,
                    startY: currentY,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [200, 200, 200],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        halign: 'center',
                    },
                    bodyStyles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        font: 'notoarabic',
                    },
                    margin: { top: 10, left: 10, right: 10 },
                });

                doc.save('soa_' + accountName + '_' + currentDate + '.pdf');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>Statement of Account</div>
                        </div>
                        <div className='modal_rowsection'>{project.projectdetails}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Account Name</div>
                        </div>
                        <div className='modal_rowsection'>{accountname}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={from}
                                onChange={handleFromChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={to}
                                onChange={handleToChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Subaccount
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={[{ id: '', name: 'All' }, ...project.subaccounts]} itemid={selectedsubaccountid} setItemid={setSelectedsubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Currency
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={selectedcurrencyid} setItemid={setSelectedcurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Debit</th>
                                        <th>Credit</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        statementdata.map((entry, index) => {
                                            return (
                                                <tr key={'entry' + index}>
                                                    <td><div>{entry.date}</div></td>
                                                    <td><div>{entry.type}</div></td>
                                                    <td><div>{entry.id}</div></td>
                                                    <td><div>{entry.name}</div></td>
                                                    <td><div>{entry.debitcurrency + ' ' + entry.debit.toLocaleString('en-US')}</div></td>
                                                    <td><div>{entry.creditcurrency + ' ' + entry.credit.toLocaleString('en-US')}</div></td>
                                                    <td><div className='statementaccount_balance'>{entry.balancecurrency + ' ' + entry.balance.toLocaleString('en-US')}</div></td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                                <tfoot className='soa_tablefooter'>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Debit</td><td>{statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Credit</td><td>{statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Closing Balance</td><td>{statementsummary.currency + ' ' + statementsummary.total.toLocaleString('en-US')}</td></tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_buttons'>
                    <div className='modal_buttoncontainer'>
                        <ReactToPrint
                            trigger={() => (<button className="modal_button">Print</button>)}
                            content={() => printRef.current}
                        />
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={() => saveAsPDF()} disabled={loading}>{loading ? "Saving..." : "Save as PDF"}</button>
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={handleCloseModal}>Cancel</button>
                    </div>
                </div>
            </div>
        )
    }

    function ExpenseAccountSOA() {
        const [accountid, setAccountid] = useState(account.id);
        const [accountname, setAccountname] = useState(account.name);
        const [selectedsubaccountid, setSelectedsubaccountid] = useState(account.subaccountid);
        const [selectedcurrencyid, setSelectedcurrencyid] = useState(account.currencyid);
        const [statementdata, setStatementdata] = useState([]);
        const [statementsummary, setStatementsummary] = useState({ currency: '', totaldebit: '', totalcredit: '', total: '' });
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            let context = {
                accountid: accountid,
                ...project,
                fromts: fromts,
                tots: tots,
                selectedsubaccountid: selectedsubaccountid,
                selectedcurrencyid: selectedcurrencyid
            }
            const expenseaccountsoa = getExpenseAccountSOA(context);
            setStatementdata(expenseaccountsoa.data);
            setStatementsummary(expenseaccountsoa.summary);
        }, [accountid, project.transactions, project.journalentries, project.exchangerates, project.currencies, fromts, tots, selectedsubaccountid, selectedcurrencyid]);

        const saveAsPDF = async () => {
            setLoading(true);
            try {
                const accountName = accountname.replace(/\s+/g, '_').toLowerCase();
                const currentDate = moment().format('DD_MM_YYYY');
                const currencyName = project.currencies.find(cur => cur.id == selectedcurrencyid)?.name;
                const subaccountName = (project.subaccounts.find(sub => sub.id == selectedsubaccountid)?.name || 'All');

                const doc = new jsPDF();
                registerArabicFont(doc);

                let currentY = 10;

                currentY += 10;
                doc.setFontSize(24);
                const pageWidth = doc.internal.pageSize.getWidth();
                const title = 'Statement of Account';
                const titleWidth = doc.getTextWidth(title);
                doc.text(title, (pageWidth - titleWidth) / 2, currentY);

                currentY += 10;
                doc.setFontSize(10);
                doc.text('Project Name: ' + project.name, 10, currentY);

                currentY += 5;
                doc.text('Account Name: ' + accountname, 10, currentY);

                currentY += 5;
                const formattedFrom = moment.unix(fromts).format('D/MMM/YYYY');
                const formattedTo = moment.unix(tots).format('D/MMM/YYYY');
                doc.text('From: ' + formattedFrom, 10, currentY);

                currentY += 5;
                doc.text('To: ' + formattedTo, 10, currentY);

                currentY += 5;
                doc.text('Subaccount: ' + subaccountName, 10, currentY);

                currentY += 5;
                doc.text('Currency: ' + currencyName, 10, currentY);

                currentY += 5;
                const body = statementdata.map(entry => [
                    entry.ts,
                    entry.type,
                    entry.id,
                    entry.name,
                    entry.debitcurrency + ' ' + entry.debitamount.toLocaleString('en-US'),
                    entry.creditcurrency + ' ' + entry.creditamount.toLocaleString('en-US'),
                    entry.balancecurrency + ' ' + entry.balanceamount.toLocaleString('en-US'),
                ]);

                body.push(
                    [
                        { content: 'Total Debit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Total Credit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Closing Balance', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.closingbalance.toLocaleString('en-US'),
                    ]
                );

                autoTable(doc, {
                    head: [['Date', 'Type', 'ID', 'Name', 'Debit', 'Credit', 'Balance']],
                    body,
                    startY: currentY,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [200, 200, 200],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        halign: 'center',
                    },
                    bodyStyles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        font: 'notoarabic',
                    },
                    margin: { top: 10, left: 10, right: 10 },
                });

                doc.save('soa_' + accountName + '_' + currentDate + '.pdf');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>Statement of Account</div>
                        </div>
                        <div className='modal_rowsection'>{project.projectdetails}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Account Name</div>
                        </div>
                        <div className='modal_rowsection'>{accountname}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={from}
                                onChange={handleFromChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={to}
                                onChange={handleToChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Subaccount
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={[{ id: '', name: 'All' }, ...project.subaccounts]} itemid={selectedsubaccountid} setItemid={setSelectedsubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Currency
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={selectedcurrencyid} setItemid={setSelectedcurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Debit</th>
                                        <th>Credit</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        statementdata.map((entry, index) => {
                                            return (
                                                <tr key={'entry' + index}>
                                                    <td><div>{entry.date}</div></td>
                                                    <td><div>{entry.type}</div></td>
                                                    <td><div>{entry.id}</div></td>
                                                    <td><div>{entry.name}</div></td>
                                                    <td><div>{entry.debitcurrency + ' ' + entry.debit.toLocaleString('en-US')}</div></td>
                                                    <td><div>{entry.creditcurrency + ' ' + entry.credit.toLocaleString('en-US')}</div></td>
                                                    <td><div className='statementaccount_balance'>{entry.balancecurrency + ' ' + entry.balance.toLocaleString('en-US')}</div></td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                                <tfoot className='soa_tablefooter'>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Debit</td><td>{statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Credit</td><td>{statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Closing Balance</td><td>{statementsummary.currency + ' ' + statementsummary.total.toLocaleString('en-US')}</td></tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_buttons'>
                    <div className='modal_buttoncontainer'>
                        <ReactToPrint
                            trigger={() => (<button className="modal_button">Print</button>)}
                            content={() => printRef.current}
                        />
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={() => saveAsPDF()} disabled={loading}>{loading ? "Saving..." : "Save as PDF"}</button>
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={handleCloseModal}>Cancel</button>
                    </div>
                </div>
            </div>
        )
    }

    function CashAccountSOA() {
        const [accountid, setAccountid] = useState(account.id);
        const [accountname, setAccountname] = useState(account.name);
        const [selectedcurrencyid, setSelectedcurrencyid] = useState(account.currencyid);
        const [statementdata, setStatementdata] = useState([]);
        const [statementsummary, setStatementsummary] = useState({ currency: '', totaldebit: '', totalcredit: '', total: '' });
        const [reconciliation, setReconciliation] = useState(false);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            let context = {
                accountid: accountid,
                ...project,
                fromts: fromts,
                tots: tots,
                selectedcurrencyid: selectedcurrencyid
            }
            const cashaccountsoa = getCashAccountSOA(context);
            setStatementdata(cashaccountsoa.data);
            setStatementsummary(cashaccountsoa.summary);
        }, [accountid, project.transactions, project.interaccounttransfers, project.currencies, project.reconciliations, project.exchangerates, from, to, selectedcurrencyid]);

        const saveAsPDF = async () => {
            setLoading(true);
            try {
                const accountName = accountname.replace(/\s+/g, '_').toLowerCase();
                const currentDate = moment().format('DD_MM_YYYY');
                const currencyName = project.currencies.find(cur => cur.id == selectedcurrencyid)?.name;

                const doc = new jsPDF();
                registerArabicFont(doc);

                let currentY = 10;

                currentY += 10;
                doc.setFontSize(24);
                const pageWidth = doc.internal.pageSize.getWidth();
                const title = 'Statement of Account';
                const titleWidth = doc.getTextWidth(title);
                doc.text(title, (pageWidth - titleWidth) / 2, currentY);

                currentY += 10;
                doc.setFontSize(10);
                doc.text('Project Name: ' + project.name, 10, currentY);

                currentY += 5;
                doc.text('Account Name: ' + accountname, 10, currentY);

                currentY += 5;
                const formattedFrom = moment.unix(fromts).format('D/MMM/YYYY');
                const formattedTo = moment.unix(tots).format('D/MMM/YYYY');
                doc.text('From: ' + formattedFrom, 10, currentY);

                currentY += 5;
                doc.text('To: ' + formattedTo, 10, currentY);

                currentY += 5;
                doc.text('Currency: ' + currencyName, 10, currentY);

                currentY += 5;
                const body = statementdata.map(entry => [
                    entry.ts,
                    entry.type,
                    entry.id,
                    entry.type == 'RCPT' ? project.clients.find(cl => cl.id == entry.stakeholder)?.name : (entry.type == 'PMNT' ? project.suppliers.find(sup => sup.id == entry.stakeholder)?.name : ''),
                    entry.name,
                    entry.debitcurrency + ' ' + entry.debitamount.toLocaleString('en-US'),
                    entry.creditcurrency + ' ' + entry.creditamount.toLocaleString('en-US'),
                    entry.balancecurrency + ' ' + entry.balanceamount.toLocaleString('en-US'),
                ]);

                body.push(
                    [
                        { content: 'Total Debit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Total Credit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Closing Balance', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.closingbalance.toLocaleString('en-US'),
                    ]
                );

                autoTable(doc, {
                    head: [['Date', 'Type', 'ID', 'Stakeholder', 'Name', 'Debit', 'Credit', 'Balance']],
                    body,
                    startY: currentY,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [200, 200, 200],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        halign: 'center',
                    },
                    bodyStyles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        font: 'notoarabic',
                    },
                    margin: { top: 10, left: 10, right: 10 },
                });

                doc.save('soa_' + accountName + '_' + currentDate + '.pdf');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>Statement of Account</div>
                        </div>
                        <div className='modal_rowsection'>{project.projectdetails}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Account Name</div>
                        </div>
                        <div className='modal_rowsection'>{accountname}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={from}
                                onChange={handleFromChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={to}
                                onChange={handleToChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Currency
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={selectedcurrencyid} setItemid={setSelectedcurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <label className='modal_label' htmlFor="reconciliation">
                                Reconciliation
                            </label>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                type="checkbox"
                                id="reconciliation"
                                checked={reconciliation}
                                onChange={(e) => setReconciliation(e.target.checked)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>ID</th>
                                        <th>Stakeholder</th>
                                        <th>Name</th>
                                        <th>Debit</th>
                                        <th>Credit</th>
                                        <th>Balance</th>
                                        {reconciliation && <th>Reconciliation</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        statementdata.map((entry, index) => {
                                            return (
                                                <tr key={'entry' + index}>
                                                    <td><div>{entry.date}</div></td>
                                                    <td><div>{entry.type}</div></td>
                                                    <td><div>{entry.id}</div></td>
                                                    <td><div>{entry.from}</div></td>
                                                    <td><div>{entry.name}</div></td>
                                                    <td><div>{entry.debitcurrency + ' ' + entry.debit.toLocaleString('en-US')}</div></td>
                                                    <td><div>{entry.creditcurrency + ' ' + entry.credit.toLocaleString('en-US')}</div></td>
                                                    <td><div className='statementaccount_balance'>{entry.balancecurrency + ' ' + entry.balance.toLocaleString('en-US')}</div></td>
                                                    {reconciliation && <td><div>{entry.reconciliationstatus}</div></td>}
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                                <tfoot className='soa_tablefooter'>
                                    <tr><td colSpan={5}></td><td colSpan={2}>Total Debit</td><td>{statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US')}</td>{reconciliation && <td></td>}</tr>
                                    <tr><td colSpan={5}></td><td colSpan={2}>Total Credit</td><td>{statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US')}</td>{reconciliation && <td></td>}</tr>
                                    <tr><td colSpan={5}></td><td colSpan={2}>Closing Balance</td><td>{statementsummary.currency + ' ' + statementsummary.total.toLocaleString('en-US')}</td>{reconciliation && <td></td>}</tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_buttons'>
                    <div className='modal_buttoncontainer'>
                        <ReactToPrint
                            trigger={() => (<button className="modal_button">Print</button>)}
                            content={() => printRef.current}
                        />
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={() => saveAsPDF()} disabled={loading}>{loading ? "Saving..." : "Save as PDF"}</button>
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={handleCloseModal}>Cancel</button>
                    </div>
                </div>
            </div>
        )
    }

    function ReceivablesSOA() {
        const [accountname, setAccountname] = useState(account.name);
        const [selectedcashaccountid, setSelectedcashaccountid] = useState(account.cashaccount);
        const [selectedsubaccountid, setSelectedsubaccountid] = useState(account.subaccountid);
        const [selectedcurrencyid, setSelectedcurrencyid] = useState(account.currencyid);
        const [statementdata, setStatementdata] = useState([]);
        const [statementsummary, setStatementsummary] = useState({ currency: '', totaldebit: '', totalcredit: '', total: '' });
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            let context = {
                ...project,
                fromts: fromts,
                tots: tots,
                selectedcashaccountid: selectedcashaccountid,
                selectedsubaccountid: selectedsubaccountid,
                selectedcurrencyid: selectedcurrencyid
            }
            const receiptsdata = getReceiptsSOA(context);
            setStatementdata(receiptsdata.data);
            setStatementsummary(receiptsdata.summary);
        }, [project.clients, project.transactions, project.cashaccounts, project.subaccounts, project.exchangerates, project.currencies, fromts, tots, selectedcashaccountid, selectedsubaccountid, selectedcurrencyid]);

        const saveAsPDF = () => {
            setLoading(true);
            try {
                const accountName = accountname.replace(/\s+/g, '_').toLowerCase();
                const currentDate = moment().format('DD_MM_YYYY');
                const currencyName = project.currencies.find(cur => cur.id == selectedcurrencyid)?.name;
                const cashaccountName = (project.cashaccounts.find(sub => sub.id == selectedcashaccountid)?.name || 'All');
                const subaccountName = (project.subaccounts.find(sub => sub.id == selectedsubaccountid)?.name || 'All');

                const doc = new jsPDF();
                registerArabicFont(doc);

                let currentY = 10;

                currentY += 10;
                doc.setFontSize(24);
                const pageWidth = doc.internal.pageSize.getWidth();
                const title = 'Statement of Account';
                const titleWidth = doc.getTextWidth(title);
                doc.text(title, (pageWidth - titleWidth) / 2, currentY);

                currentY += 10;
                doc.setFontSize(10);
                doc.text('Project Name: ' + project.name, 10, currentY);

                currentY += 5;
                doc.text('Account Name: ' + accountname, 10, currentY);

                currentY += 5;
                const formattedFrom = moment.unix(fromts).format('D/MMM/YYYY');
                const formattedTo = moment.unix(tots).format('D/MMM/YYYY');
                doc.text('From: ' + formattedFrom, 10, currentY);

                currentY += 5;
                doc.text('To: ' + formattedTo, 10, currentY);

                currentY += 5;
                doc.text('Cash Account: ' + cashaccountName, 10, currentY);

                currentY += 5;
                doc.text('Subaccount: ' + subaccountName, 10, currentY);

                currentY += 5;
                doc.text('Currency: ' + currencyName, 10, currentY);

                currentY += 5;
                const body = statementdata.map(entry => [
                    entry.ts,
                    entry.type,
                    entry.id,
                    entry.clientname,
                    entry.name,
                    entry.debitcurrency + ' ' + entry.debitamount.toLocaleString('en-US'),
                    entry.creditcurrency + ' ' + entry.creditamount.toLocaleString('en-US'),
                    entry.balancecurrency + ' ' + entry.balanceamount.toLocaleString('en-US'),
                ]);

                body.push(
                    [
                        { content: 'Total Debit', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Total Credit', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Closing Balance', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.closingbalance.toLocaleString('en-US'),
                    ]
                );

                autoTable(doc, {
                    head: [['Date', 'Type', 'ID', 'From', 'Name', 'Debit', 'Credit', 'Balance']],
                    body,
                    startY: currentY,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [200, 200, 200],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        halign: 'center',
                    },
                    bodyStyles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        font: 'notoarabic',
                    },
                    margin: { top: 10, left: 10, right: 10 },
                });

                doc.save('soa_' + accountName + '_' + currentDate + '.pdf');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>Statement of Account</div>
                        </div>
                        <div className='modal_rowsection'>{project.projectdetails}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Account Name</div>
                        </div>
                        <div className='modal_rowsection'>{accountname}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={from}
                                onChange={handleFromChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={to}
                                onChange={handleToChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Cash Account
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.cashaccounts} itemid={selectedcashaccountid} setItemid={setSelectedcashaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Subaccount
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={[{ id: '', name: 'All' }, ...project.subaccounts]} itemid={selectedsubaccountid} setItemid={setSelectedsubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Currency
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={selectedcurrencyid} setItemid={setSelectedcurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>ID</th>
                                        <th>From</th>
                                        <th>Name</th>
                                        <th>Debit</th>
                                        <th>Credit</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        statementdata.map((entry, index) => {
                                            return (
                                                <tr key={'entry' + index} style={{ pageBreakInside: 'avoid' }}>
                                                    <td><div>{entry.ts}</div></td>
                                                    <td><div>{entry.type}</div></td>
                                                    <td><div>{entry.id}</div></td>
                                                    <td><div>{entry.clientname}</div></td>
                                                    <td><div>{entry.name}</div></td>
                                                    <td><div>{entry.debitcurrency + ' ' + entry.debitamount.toLocaleString('en-US')}</div></td>
                                                    <td><div>{entry.creditcurrency + ' ' + entry.creditamount.toLocaleString('en-US')}</div></td>
                                                    <td><div className='statementaccount_balance'>{entry.balancecurrency + ' ' + entry.balanceamount.toLocaleString('en-US')}</div></td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                                <tfoot className='soa_tablefooter'>
                                    <tr><td colSpan={5}></td><td colSpan={2}>Total Debit</td><td>{statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={5}></td><td colSpan={2}>Total Credit</td><td>{statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={5}></td><td colSpan={2}>Closing Balance</td><td>{statementsummary.currency + ' ' + statementsummary.closingbalance.toLocaleString('en-US')}</td></tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_buttons'>
                    <div className='modal_buttoncontainer'>
                        <ReactToPrint
                            trigger={() => (<button className="modal_button">Print</button>)}
                            content={() => printRef.current}
                        />
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={() => saveAsPDF()} disabled={loading}>{loading ? "Saving..." : "Save as PDF"}</button>
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={handleCloseModal}>Cancel</button>
                    </div>
                </div>
            </div>
        )
    }

    function ItemsSOA() {
        const [accountid, setAccountid] = useState(account.id);
        const [accountname, setAccountname] = useState(account.name);
        const [selectedsubaccountid, setSelectedsubaccountid] = useState(account.subaccountid);
        const [statementdata, setStatementdata] = useState([]);
        const [statementsummary, setStatementsummary] = useState({ currency: '', totaldebit: '', totalcredit: '', total: '' });
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const context = {
                accountid: account.id,
                ...project,
                fromts: fromts,
                tots: tots,
                selectedsubaccountid: selectedsubaccountid
            };
            let getitemsdata = getItemsSOA(context);
            setStatementdata(getitemsdata.data);
            setStatementsummary(getitemsdata.summary);
        }, [account.id, project, fromts, tots, selectedsubaccountid]);

        const saveAsPDF = () => {
            setLoading(true);
            try {
                const accountName = accountname.replace(/\s+/g, '_').toLowerCase();
                const currentDate = moment().format('DD_MM_YYYY');
                const subaccountName = (project.subaccounts.find(sub => sub.id == selectedsubaccountid)?.name || 'All');

                const doc = new jsPDF();
                registerArabicFont(doc);

                let currentY = 10;

                currentY += 10;
                doc.setFontSize(24);
                const pageWidth = doc.internal.pageSize.getWidth();
                const title = 'Statement of Account';
                const titleWidth = doc.getTextWidth(title);
                doc.text(title, (pageWidth - titleWidth) / 2, currentY);

                currentY += 10;
                doc.setFontSize(10);
                doc.text('Project Name: ' + project.name, 10, currentY);

                currentY += 5;
                doc.text('Account Name: ' + accountname, 10, currentY);

                currentY += 5;
                const formattedFrom = moment.unix(fromts).format('D/MMM/YYYY');
                const formattedTo = moment.unix(tots).format('D/MMM/YYYY');
                doc.text('From: ' + formattedFrom, 10, currentY);

                currentY += 5;
                doc.text('To: ' + formattedTo, 10, currentY);

                currentY += 5;
                doc.text('Subaccount: ' + subaccountName, 10, currentY);

                currentY += 5;
                const body = statementdata.map(entry => [
                    entry.ts,
                    entry.type,
                    entry.id,
                    entry.clientname,
                    entry.name,
                    entry.debitcurrency + ' ' + entry.debitamount.toLocaleString('en-US'),
                    entry.creditcurrency + ' ' + entry.creditamount.toLocaleString('en-US'),
                    entry.balancecurrency + ' ' + entry.balanceamount.toLocaleString('en-US'),
                ]);

                body.push(
                    [
                        { content: 'Total Debit', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Total Credit', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Closing Balance', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.closingbalance.toLocaleString('en-US'),
                    ]
                );

                autoTable(doc, {
                    head: [['Date', 'Type', 'ID', 'From', 'Name', 'Debit', 'Credit', 'Balance']],
                    body,
                    startY: currentY,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [200, 200, 200],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        halign: 'center',
                    },
                    bodyStyles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        font: 'notoarabic',
                    },
                    margin: { top: 10, left: 10, right: 10 },
                });

                doc.save('soa_' + accountName + '_' + currentDate + '.pdf');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>Statement of Account</div>
                        </div>
                        <div className='modal_rowsection'>{project.projectdetails}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Account Name</div>
                        </div>
                        <div className='modal_rowsection'>{accountname}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={from}
                                onChange={handleFromChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={to}
                                onChange={handleToChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Subaccount
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={[{ id: '', name: 'All' }, ...project.subaccounts]} itemid={selectedsubaccountid} setItemid={setSelectedsubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Debit</th>
                                        <th>Credit</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        statementdata.map((entry, index) => {
                                            return (
                                                <tr key={'entry' + index} style={{ pageBreakInside: 'avoid' }}>
                                                    <td><div>{entry.date}</div></td>
                                                    <td><div>{entry.type}</div></td>
                                                    <td><div>{entry.id}</div></td>
                                                    <td><div>{entry.name}</div></td>
                                                    <td><div>{entry.debitamount}</div></td>
                                                    <td><div>{entry.creditamount}</div></td>
                                                    <td><div>{entry.balanceamount}</div></td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                                <tfoot className='soa_tablefooter'>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Debit</td><td>{statementsummary.totaldebit}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Credit</td><td>{statementsummary.totalcredit}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Closing Balance</td><td>{statementsummary.closingbalance}</td></tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_buttons'>
                    <div className='modal_buttoncontainer'>
                        <ReactToPrint
                            trigger={() => (<button className="modal_button">Print</button>)}
                            content={() => printRef.current}
                        />
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={() => saveAsPDF()} disabled={loading}>{loading ? "Saving..." : "Save as PDF"}</button>
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={handleCloseModal}>Cancel</button>
                    </div>
                </div>
            </div>
        )
    }

    function LiabilitySOA() {
        const [accountid, setAccountid] = useState(account.id);
        const [accountname, setAccountname] = useState(account.name);
        const [selectedsubaccountid, setSelectedsubaccountid] = useState(account.subaccountid);
        const [selectedcurrencyid, setSelectedcurrencyid] = useState(account.currencyid);
        const [statementdata, setStatementdata] = useState([]);
        const [statementsummary, setStatementsummary] = useState({ currency: '', totaldebit: '', totalcredit: '', total: '' });
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const context = {
                accountid: account.id,
                ...project,
                fromts: fromts,
                tots: tots,
                selectedsubaccountid: selectedsubaccountid,
                selectedcurrencyid: selectedcurrencyid
            };
            const liabilityaccountsoa = getLiabilityAccountSOA(context);
            setStatementdata(liabilityaccountsoa.data);
            setStatementsummary(liabilityaccountsoa.summary);
        }, [accountid, project.transactions, project.journalentries, project.exchangerates, project.currencies, from, to, selectedsubaccountid, selectedcurrencyid]);

        const saveAsPDF = async () => {
            setLoading(true);
            try {
                const accountName = accountname.replace(/\s+/g, '_').toLowerCase();
                const currentDate = moment().format('DD_MM_YYYY');
                const currencyName = project.currencies.find(cur => cur.id == selectedcurrencyid)?.name;
                const subaccountName = (project.subaccounts.find(sub => sub.id == selectedsubaccountid)?.name || 'All');

                const doc = new jsPDF();
                registerArabicFont(doc);

                let currentY = 10;

                currentY += 10;
                doc.setFontSize(24);
                const pageWidth = doc.internal.pageSize.getWidth();
                const title = 'Statement of Account';
                const titleWidth = doc.getTextWidth(title);
                doc.text(title, (pageWidth - titleWidth) / 2, currentY);

                currentY += 10;
                doc.setFontSize(10);
                doc.text('Project Name: ' + project.name, 10, currentY);

                currentY += 5;
                doc.text('Account Name: ' + accountname, 10, currentY);

                currentY += 5;
                const formattedFrom = moment.unix(fromts).format('D/MMM/YYYY');
                const formattedTo = moment.unix(tots).format('D/MMM/YYYY');
                doc.text('From: ' + formattedFrom, 10, currentY);

                currentY += 5;
                doc.text('To: ' + formattedTo, 10, currentY);

                currentY += 5;
                doc.text('Subaccount: ' + subaccountName, 10, currentY);

                currentY += 5;
                doc.text('Currency: ' + currencyName, 10, currentY);

                currentY += 5;
                const body = statementdata.map(entry => [
                    entry.ts,
                    entry.type,
                    entry.id,
                    entry.name,
                    entry.debitcurrency + ' ' + entry.debitamount.toLocaleString('en-US'),
                    entry.creditcurrency + ' ' + entry.creditamount.toLocaleString('en-US'),
                    entry.balancecurrency + ' ' + entry.balanceamount.toLocaleString('en-US'),
                ]);

                body.push(
                    [
                        { content: 'Total Debit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Total Credit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Closing Balance', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.closingbalance.toLocaleString('en-US'),
                    ]
                );

                autoTable(doc, {
                    head: [['Date', 'Type', 'ID', 'Name', 'Debit', 'Credit', 'Balance']],
                    body,
                    startY: currentY,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [200, 200, 200],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        halign: 'center',
                    },
                    bodyStyles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        font: 'notoarabic',
                    },
                    margin: { top: 10, left: 10, right: 10 },
                });

                doc.save('soa_' + accountName + '_' + currentDate + '.pdf');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>Statement of Account</div>
                        </div>
                        <div className='modal_rowsection'>{project.projectdetails}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Account Name</div>
                        </div>
                        <div className='modal_rowsection'>{accountname}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={from}
                                onChange={handleFromChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={to}
                                onChange={handleToChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Subaccount
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={[{ id: '', name: 'All' }, ...project.subaccounts]} itemid={selectedsubaccountid} setItemid={setSelectedsubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Currency
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={selectedcurrencyid} setItemid={setSelectedcurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Debit</th>
                                        <th>Credit</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        statementdata.map((entry, index) => {
                                            return (
                                                <tr key={'entry' + index}>
                                                    <td><div>{entry.date}</div></td>
                                                    <td><div>{entry.type}</div></td>
                                                    <td><div>{entry.id}</div></td>
                                                    <td><div>{entry.name}</div></td>
                                                    <td><div>{entry.debitcurrency + ' ' + entry.debit.toLocaleString('en-US')}</div></td>
                                                    <td><div>{entry.creditcurrency + ' ' + entry.credit.toLocaleString('en-US')}</div></td>
                                                    <td><div className='statementaccount_balance'>{entry.balancecurrency + ' ' + entry.balance.toLocaleString('en-US')}</div></td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                                <tfoot className='soa_tablefooter'>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Debit</td><td>{statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Credit</td><td>{statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Closing Balance</td><td>{statementsummary.currency + ' ' + statementsummary.total.toLocaleString('en-US')}</td></tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_buttons'>
                    <div className='modal_buttoncontainer'>
                        <ReactToPrint
                            trigger={() => (<button className="modal_button">Print</button>)}
                            content={() => printRef.current}
                        />
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={() => saveAsPDF()} disabled={loading}>{loading ? "Saving..." : "Save as PDF"}</button>
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={handleCloseModal}>Cancel</button>
                    </div>
                </div>
            </div>
        )
    }

    function StaffSOA() {
        const [accountid, setAccountid] = useState(account.id);
        const [accountname, setAccountname] = useState(account.name);
        const [selectedsubaccountid, setSelectedsubaccountid] = useState(account.subaccountid);
        const [selectedcurrencyid, setSelectedcurrencyid] = useState(account.currencyid);
        const [statementdata, setStatementdata] = useState([]);
        const [statementsummary, setStatementsummary] = useState({ currency: '', totaldebit: '', totalcredit: '', total: '' });

        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const context = {
                accountid: account.id,
                ...project,
                fromts: fromts,
                tots: tots,
                selectedsubaccountid: selectedsubaccountid,
                selectedcurrencyid: selectedcurrencyid
            };
            const staffaccountsoa = getStaffSOA(context);

            setStatementdata(staffaccountsoa.data);
            setStatementsummary(staffaccountsoa.summary);
        }, [accountid, project.transactions, project.journalentries, project.exchangerates, project.currencies, from, to, selectedsubaccountid, selectedcurrencyid]);

        const saveAsPDF = () => {
            setLoading(true);
            try {
                const accountName = accountname.replace(/\s+/g, '_').toLowerCase();
                const currentDate = moment().format('DD_MM_YYYY');
                const currencyName = project.currencies.find(cur => cur.id == selectedcurrencyid)?.name;
                const subaccountName = (project.subaccounts.find(sub => sub.id == selectedsubaccountid)?.name || 'All');

                const doc = new jsPDF();
                registerArabicFont(doc);

                let currentY = 10;

                currentY += 10;
                doc.setFontSize(24);
                const pageWidth = doc.internal.pageSize.getWidth();
                const title = 'Statement of Account';
                const titleWidth = doc.getTextWidth(title);
                doc.text(title, (pageWidth - titleWidth) / 2, currentY);

                currentY += 10;
                doc.setFontSize(10);
                doc.text('Project Name: ' + project.name, 10, currentY);

                currentY += 5;
                doc.text('Account Name: ' + accountname, 10, currentY);

                currentY += 5;
                const formattedFrom = moment.unix(fromts).format('D/MMM/YYYY');
                const formattedTo = moment.unix(tots).format('D/MMM/YYYY');
                doc.text('From: ' + formattedFrom, 10, currentY);

                currentY += 5;
                doc.text('To: ' + formattedTo, 10, currentY);

                currentY += 5;
                doc.text('Subaccount: ' + subaccountName, 10, currentY);

                currentY += 5;
                doc.text('Currency: ' + currencyName, 10, currentY);

                currentY += 5;
                const body = statementdata.map(entry => [
                    entry.ts,
                    entry.type,
                    entry.id,
                    entry.name,
                    entry.debitcurrency + ' ' + entry.debitamount.toLocaleString('en-US'),
                    entry.creditcurrency + ' ' + entry.creditamount.toLocaleString('en-US'),
                    entry.balancecurrency + ' ' + entry.balanceamount.toLocaleString('en-US'),
                ]);

                body.push(
                    [
                        { content: 'Total Debit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Total Credit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Closing Balance', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.closingbalance.toLocaleString('en-US'),
                    ]
                );

                autoTable(doc, {
                    head: [['Date', 'Type', 'ID', 'Name', 'Debit', 'Credit', 'Balance']],
                    body,
                    startY: currentY,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [200, 200, 200],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        halign: 'center',
                    },
                    bodyStyles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        font: 'notoarabic',
                    },
                    margin: { top: 10, left: 10, right: 10 },
                });

                doc.save('soa_' + accountName + '_' + currentDate + '.pdf');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>Statement of Account</div>
                        </div>
                        <div className='modal_rowsection'>{project.projectdetails}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Account Name</div>
                        </div>
                        <div className='modal_rowsection'>{accountname}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={from}
                                onChange={handleFromChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={to}
                                onChange={handleToChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Subaccount
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={[{ id: '', name: 'All' }, ...project.subaccounts]} itemid={selectedsubaccountid} setItemid={setSelectedsubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Currency
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={selectedcurrencyid} setItemid={setSelectedcurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Debit</th>
                                        <th>Credit</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        statementdata.map((entry, index) => {
                                            return (
                                                <tr key={'entry' + index} style={{ pageBreakInside: 'avoid' }}>
                                                    <td><div>{entry.date}</div></td>
                                                    <td><div>{entry.type}</div></td>
                                                    <td><div>{entry.id}</div></td>
                                                    <td><div>{entry.name}</div></td>
                                                    <td><div>{entry.debitcurrency + ' ' + entry.debit.toLocaleString('en-US')}</div></td>
                                                    <td><div>{entry.creditcurrency + ' ' + entry.credit.toLocaleString('en-US')}</div></td>
                                                    <td><div className='statementaccount_balance'>{entry.balancecurrency + ' ' + entry.balance.toLocaleString('en-US')}</div></td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                                <tfoot className='soa_tablefooter'>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Debit</td><td>{statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Credit</td><td>{statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Closing Balance</td><td>{statementsummary.currency + ' ' + statementsummary.total.toLocaleString('en-US')}</td></tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_buttons'>
                    <div className='modal_buttoncontainer'>
                        <ReactToPrint
                            trigger={() => (<button className="modal_button">Print</button>)}
                            content={() => printRef.current}
                        />
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={() => saveAsPDF()} disabled={loading}>{loading ? "Saving..." : "Save as PDF"}</button>
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={handleCloseModal}>Cancel</button>
                    </div>
                </div>
            </div>
        )
    }

    function AssetSOA() {
        const [accountid, setAccountid] = useState(account.id);
        const [accountname, setAccountname] = useState(account.name);
        const [selectedsubaccountid, setSelectedsubaccountid] = useState(account.subaccountid);
        const [selectedcurrencyid, setSelectedcurrencyid] = useState(account.currencyid);
        const [statementdata, setStatementdata] = useState([]);
        const [statementsummary, setStatementsummary] = useState({ currency: '', totaldebit: '', totalcredit: '', total: '' });
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const context = {
                accountid: account.id,
                ...project,
                fromts: fromts,
                tots: tots,
                selectedsubaccountid: selectedsubaccountid,
                selectedcurrencyid: selectedcurrencyid
            };
            const assetaccountsoa = getAssetAccountSOA(context);
            setStatementdata(assetaccountsoa.data);
            setStatementsummary(assetaccountsoa.summary);
        }, [accountid, project.transactions, project.journalentries, project.exchangerates, project.currencies, from, to, selectedsubaccountid, selectedcurrencyid]);

        const saveAsPDF = async () => {
            setLoading(true);
            try {
                const accountName = accountname.replace(/\s+/g, '_').toLowerCase();
                const currentDate = moment().format('DD_MM_YYYY');
                const currencyName = project.currencies.find(cur => cur.id == selectedcurrencyid)?.name;
                const subaccountName = (project.subaccounts.find(sub => sub.id == selectedsubaccountid)?.name || 'All');

                const doc = new jsPDF();
                registerArabicFont(doc);

                let currentY = 10;

                currentY += 10;
                doc.setFontSize(24);
                const pageWidth = doc.internal.pageSize.getWidth();
                const title = 'Statement of Account';
                const titleWidth = doc.getTextWidth(title);
                doc.text(title, (pageWidth - titleWidth) / 2, currentY);

                currentY += 10;
                doc.setFontSize(10);
                doc.text('Project Name: ' + project.name, 10, currentY);

                currentY += 5;
                doc.text('Account Name: ' + accountname, 10, currentY);

                currentY += 5;
                const formattedFrom = moment.unix(fromts).format('D/MMM/YYYY');
                const formattedTo = moment.unix(tots).format('D/MMM/YYYY');
                doc.text('From: ' + formattedFrom, 10, currentY);

                currentY += 5;
                doc.text('To: ' + formattedTo, 10, currentY);

                currentY += 5;
                doc.text('Subaccount: ' + subaccountName, 10, currentY);

                currentY += 5;
                doc.text('Currency: ' + currencyName, 10, currentY);

                currentY += 5;
                const body = statementdata.map(entry => [
                    entry.ts,
                    entry.type,
                    entry.id,
                    entry.name,
                    entry.debitcurrency + ' ' + entry.debitamount.toLocaleString('en-US'),
                    entry.creditcurrency + ' ' + entry.creditamount.toLocaleString('en-US'),
                    entry.balancecurrency + ' ' + entry.balanceamount.toLocaleString('en-US'),
                ]);

                body.push(
                    [
                        { content: 'Total Debit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Total Credit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Closing Balance', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.closingbalance.toLocaleString('en-US'),
                    ]
                );

                autoTable(doc, {
                    head: [['Date', 'Type', 'ID', 'Name', 'Debit', 'Credit', 'Balance']],
                    body,
                    startY: currentY,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [200, 200, 200],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        halign: 'center',
                    },
                    bodyStyles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        font: 'notoarabic',
                    },
                    margin: { top: 10, left: 10, right: 10 },
                });

                doc.save('soa_' + accountName + '_' + currentDate + '.pdf');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>Statement of Account</div>
                        </div>
                        <div className='modal_rowsection'>{project.projectdetails}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Account Name</div>
                        </div>
                        <div className='modal_rowsection'>{accountname}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={from}
                                onChange={handleFromChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={to}
                                onChange={handleToChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Subaccount
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={[{ id: '', name: 'All' }, ...project.subaccounts]} itemid={selectedsubaccountid} setItemid={setSelectedsubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Currency
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={selectedcurrencyid} setItemid={setSelectedcurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Debit</th>
                                        <th>Credit</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        statementdata.map((entry, index) => {
                                            return (
                                                <tr key={'entry' + index}>
                                                    <td><div>{entry.date}</div></td>
                                                    <td><div>{entry.type}</div></td>
                                                    <td><div>{entry.id}</div></td>
                                                    <td><div>{entry.name}</div></td>
                                                    <td><div>{entry.debitcurrency + ' ' + entry.debit.toLocaleString('en-US')}</div></td>
                                                    <td><div>{entry.creditcurrency + ' ' + entry.credit.toLocaleString('en-US')}</div></td>
                                                    <td><div className='statementaccount_balance'>{entry.balancecurrency + ' ' + entry.balance.toLocaleString('en-US')}</div></td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                                <tfoot className='soa_tablefooter'>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Debit</td><td>{statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Credit</td><td>{statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Closing Balance</td><td>{statementsummary.currency + ' ' + statementsummary.total.toLocaleString('en-US')}</td></tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_buttons'>
                    <div className='modal_buttoncontainer'>
                        <ReactToPrint
                            trigger={() => (<button className="modal_button">Print</button>)}
                            content={() => printRef.current}
                        />
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={() => saveAsPDF()} disabled={loading}>{loading ? "Saving..." : "Save as PDF"}</button>
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={handleCloseModal}>Cancel</button>
                    </div>
                </div>
            </div>
        )
    }

    function EquitySOA() {
        const [accountid, setAccountid] = useState(account.id);
        const [accountname, setAccountname] = useState(account.name);
        const [selectedsubaccountid, setSelectedsubaccountid] = useState(account.subaccountid);
        const [selectedcurrencyid, setSelectedcurrencyid] = useState(account.currencyid);
        const [statementdata, setStatementdata] = useState([]);
        const [statementsummary, setStatementsummary] = useState({ currency: '', totaldebit: '', totalcredit: '', total: '' });
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const context = {
                accountid: account.id,
                ...project,
                fromts: fromts,
                tots: tots,
                selectedcurrencyid: selectedsubaccountid,
                selectedcurrencyid: selectedcurrencyid
            };
            const equityaccountsoa = getEquityAccountSOA(context);
            setStatementdata(equityaccountsoa.data);
            setStatementsummary(equityaccountsoa.summary);
        }, [accountid, project.transactions, project.journalentries, project.exchangerates, project.currencies, from, to, selectedsubaccountid, selectedcurrencyid]);

        const saveAsPDF = async () => {
            setLoading(true);
            try {
                const accountName = accountname.replace(/\s+/g, '_').toLowerCase();
                const currentDate = moment().format('DD_MM_YYYY');
                const currencyName = project.currencies.find(cur => cur.id == selectedcurrencyid)?.name;
                const subaccountName = (project.subaccounts.find(sub => sub.id == selectedsubaccountid)?.name || 'All');

                const doc = new jsPDF();
                registerArabicFont(doc);

                let currentY = 10;

                currentY += 10;
                doc.setFontSize(24);
                const pageWidth = doc.internal.pageSize.getWidth();
                const title = 'Statement of Account';
                const titleWidth = doc.getTextWidth(title);
                doc.text(title, (pageWidth - titleWidth) / 2, currentY);

                currentY += 10;
                doc.setFontSize(10);
                doc.text('Project Name: ' + project.name, 10, currentY);

                currentY += 5;
                doc.text('Account Name: ' + accountname, 10, currentY);

                currentY += 5;
                const formattedFrom = moment.unix(fromts).format('D/MMM/YYYY');
                const formattedTo = moment.unix(tots).format('D/MMM/YYYY');
                doc.text('From: ' + formattedFrom, 10, currentY);

                currentY += 5;
                doc.text('To: ' + formattedTo, 10, currentY);

                currentY += 5;
                doc.text('Subaccount: ' + subaccountName, 10, currentY);

                currentY += 5;
                doc.text('Currency: ' + currencyName, 10, currentY);

                currentY += 5;
                const body = statementdata.map(entry => [
                    entry.ts,
                    entry.type,
                    entry.id,
                    entry.name,
                    entry.debitcurrency + ' ' + entry.debitamount.toLocaleString('en-US'),
                    entry.creditcurrency + ' ' + entry.creditamount.toLocaleString('en-US'),
                    entry.balancecurrency + ' ' + entry.balanceamount.toLocaleString('en-US'),
                ]);

                body.push(
                    [
                        { content: 'Total Debit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Total Credit', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US'),
                    ],
                    [
                        { content: 'Closing Balance', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                        statementsummary.currency + ' ' + statementsummary.closingbalance.toLocaleString('en-US'),
                    ]
                );

                autoTable(doc, {
                    head: [['Date', 'Type', 'ID', 'Name', 'Debit', 'Credit', 'Balance']],
                    body,
                    startY: currentY,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [200, 200, 200],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        halign: 'center',
                    },
                    bodyStyles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        font: 'notoarabic',
                    },
                    margin: { top: 10, left: 10, right: 10 },
                });

                doc.save('soa_' + accountName + '_' + currentDate + '.pdf');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>Statement of Account</div>
                        </div>
                        <div className='modal_rowsection'>{project.projectdetails}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Account Name</div>
                        </div>
                        <div className='modal_rowsection'>{accountname}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={from}
                                onChange={handleFromChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className="reports_datepicker"
                                selected={to}
                                onChange={handleToChange}
                                dateFormat="dd/MMM/yyyy"
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Subaccount
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={[{ id: '', name: 'All' }, ...project.subaccounts]} itemid={selectedsubaccountid} setItemid={setSelectedsubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>
                                Currency
                            </div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={selectedcurrencyid} setItemid={setSelectedcurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Debit</th>
                                        <th>Credit</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        statementdata.map((entry, index) => {
                                            return (
                                                <tr key={'entry' + index}>
                                                    <td><div>{entry.date}</div></td>
                                                    <td><div>{entry.type}</div></td>
                                                    <td><div>{entry.id}</div></td>
                                                    <td><div>{entry.name}</div></td>
                                                    <td><div>{entry.debitcurrency + ' ' + entry.debit.toLocaleString('en-US')}</div></td>
                                                    <td><div>{entry.creditcurrency + ' ' + entry.credit.toLocaleString('en-US')}</div></td>
                                                    <td><div className='statementaccount_balance'>{entry.balancecurrency + ' ' + entry.balance.toLocaleString('en-US')}</div></td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                                <tfoot className='soa_tablefooter'>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Debit</td><td>{statementsummary.currency + ' ' + statementsummary.totaldebit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Total Credit</td><td>{statementsummary.currency + ' ' + statementsummary.totalcredit.toLocaleString('en-US')}</td></tr>
                                    <tr><td colSpan={4}></td><td colSpan={2}>Closing Balance</td><td>{statementsummary.currency + ' ' + statementsummary.total.toLocaleString('en-US')}</td></tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_buttons'>
                    <div className='modal_buttoncontainer'>
                        <ReactToPrint
                            trigger={() => (<button className="modal_button">Print</button>)}
                            content={() => printRef.current}
                        />
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={() => saveAsPDF()} disabled={loading}>{loading ? "Saving..." : "Save as PDF"}</button>
                    </div>
                    <div className='modal_buttoncontainer'>
                        <button className='modal_button' onClick={handleCloseModal}>Cancel</button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            {type === 'clients' && <ClientAccountSOA />}
            {type === 'suppliers' && <SupplierAccountSOA />}
            {type === 'receivables' && <ReceivablesSOA />}
            {type === 'items' && <ItemsSOA />}
            {type === 'staff' && <StaffSOA />}
            {type === 'cashaccounts' && <CashAccountSOA />}
            {type === 'assets' && <AssetSOA />}
            {type === 'liabilityaccounts' && <LiabilitySOA />}
            {type === 'equityaccounts' && <EquitySOA />}
            {type === 'incomeaccounts' && <IncomeAccountSOA />}
            {type === 'expenseaccounts' && <ExpenseAccountSOA />}
        </>
    );
}

export default StatementAccount;